import React from 'react';
import styled, { css } from 'styled-components';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { black400, cinnabar300 } from '../../../theme/colors';
import { baseCaption } from '../../../theme/typography';
import themeSizes from '../../../theme/themeSizes';

const SelectWrapper = styled.div`
    display: ${props => props.display || 'flex'};
    margin-right: ${props => props.marginRight || '0px'};
    margin-bottom: ${props => props.marginBottom || '0px'};
    margin-left: ${props => props.marginLeft || '0px'};
    flex-direction: column;
    width: ${props => props.width || '100%'};
    max-width: 100%;
    box-sizing: border-box;
    ${props => props.isDisable && css`
       cursor:default
    `}
    @media (max-width: 995px) {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        margin-right: 0 !important;
        margin-left: 0 !important;
        box-sizing: border-box !important;
    }
`;
const SelectStyle = styled(Select)`
    width: ${props => props.width || '100%'};
    max-width: 100%;
    box-sizing: border-box;
    margin-bottom: ${props => props.marginBottom || '0px'}; 
    margin-left: ${props => props.marginLeft || '0px'};
    text-transform: ${props => props.capitalize || ""};
    :focus {
        outline: none;
    }
    & > div:first-child, & > span + div {
        min-height: ${props => props.height || '42px'};
        border: ${props => props.noBorder ? 'none' : props.hasError ? `1px solid ${cinnabar300}` : props.border || `1px solid hsl(0,0%,80%)`};
        border-radius: 8px !important;
        box-sizing: border-box !important;
        width: 100% !important;
        max-width: 100% !important;
        div {
            span {
                display: none;
            }
        }
    }
    & > div:last-child {
        span {
            display: none;
        }
    }
    @media (max-width: 995px) {
        width: 100% !important;
        max-width: 100% !important;
        margin-left: 0 !important;
        box-sizing: border-box !important;
    }
`;
const CreatableStyle = styled(Creatable)`
    width: ${props => props.width || '100%'};
    max-width: 100%;
    box-sizing: border-box;
    :focus {
        outline: none;
    }
    & > div:first-child, & > span + div {
        min-height: ${props => props.height || '42px'};
        border: ${props => props.noBorder ? 'none' : props.hasError ? `1px solid ${cinnabar300}` : props.border || `1px solid hsl(0,0%,80%)`};
        border-radius: 8px !important;
        box-sizing: border-box !important;
        width: 100% !important;
        max-width: 100% !important;
        div {
            span {
                display: none;
            }
        }
    }
    & > div:last-child {
        span {
            display: none;
        }
    }
    @media (max-width: 995px) {
        width: 100% !important;
        max-width: 100% !important;
        margin-left: 0 !important;
        box-sizing: border-box !important;
    }
`;
const ErrorMessageStyle = styled.p`
    ${baseCaption}
    color: ${cinnabar300};
    margin: 6px 0 0;
    font-size: 12px;
    font-weight: 500;
    text-align: left;
`;

export const SelectFilter = ({
    props = {},
    subText,
    warnMessage,
    rootProps,
    height,
    capitalizeFirstChar = "capitalize",
    borderRadius = 8,
    customStyles,
    containerStyles,
    dropdownColor,
    noOptionMessage,
    menuStyles,
    isCreatable = false,
    styleProps = {},

}) => {
    const SelectComponent = isCreatable ? CreatableStyle : SelectStyle;
    const portalTarget = props.menuPortalTarget !== undefined ? props.menuPortalTarget : null;
    const wrapperRef = React.useRef(null);
    const [calculatedPlacement, setCalculatedPlacement] = React.useState("bottom");

    const updatePlacement = React.useCallback(() => {
        if (props.menuPlacement && props.menuPlacement !== "auto") {
            setCalculatedPlacement(props.menuPlacement);
            return;
        }
        if (!wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return;

        // Find closest scrollable ancestor
        let parent = wrapperRef.current.parentElement;
        let scrollParent = null;
        while (parent && parent !== document.body && parent !== document.documentElement) {
            const style = window.getComputedStyle(parent);
            const overflowY = style.overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
                scrollParent = parent;
                break;
            }
            parent = parent.parentElement;
        }

        const optionCount = Array.isArray(props.options) ? props.options.length : 5;
        const estimatedHeight = Math.min(Math.max(optionCount * 38 + 16, 100), 220);

        if (scrollParent) {
            const parentRect = scrollParent.getBoundingClientRect();
            const spaceBelowInParent = parentRect.bottom - rect.bottom;
            const spaceAboveInParent = rect.top - parentRect.top;

            if (spaceBelowInParent < estimatedHeight && spaceAboveInParent > spaceBelowInParent) {
                setCalculatedPlacement("top");
                return;
            }
            if (spaceBelowInParent >= estimatedHeight) {
                setCalculatedPlacement("bottom");
                return;
            }
        }

        const spaceBelowInWindow = window.innerHeight - rect.bottom;
        const spaceAboveInWindow = rect.top;
        if (spaceBelowInWindow < estimatedHeight && spaceAboveInWindow > spaceBelowInWindow) {
            setCalculatedPlacement("top");
            return;
        }

        setCalculatedPlacement("bottom");
    }, [props.menuPlacement, props.options]);

    React.useEffect(() => {
        updatePlacement();
        const handleScrollOrResize = () => {
            updatePlacement();
        };

        window.addEventListener('resize', handleScrollOrResize);

        let parent = wrapperRef.current ? wrapperRef.current.parentElement : null;
        let scrollParent = null;
        while (parent && parent !== document.body && parent !== document.documentElement) {
            const style = window.getComputedStyle(parent);
            const overflowY = style.overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
                scrollParent = parent;
                break;
            }
            parent = parent.parentElement;
        }

        if (scrollParent) {
            scrollParent.addEventListener('scroll', handleScrollOrResize, { passive: true });
        }

        return () => {
            window.removeEventListener('resize', handleScrollOrResize);
            if (scrollParent) {
                scrollParent.removeEventListener('scroll', handleScrollOrResize);
            }
        };
    }, [updatePlacement]);

    const handleMenuOpen = () => {
        updatePlacement();
        if (props.onMenuOpen) {
            props.onMenuOpen();
        }
    };

    const effectivePlacement = props.menuPlacement && props.menuPlacement !== "auto"
        ? props.menuPlacement
        : calculatedPlacement;

    const defaultCloseOnScroll = (event) => {
        if (!event || !event.target) return true;
        if (typeof event.target.closest === 'function') {
            if (
                event.target.closest('.select-filter-component') ||
                event.target.closest('[class*="menuList"]') ||
                event.target.closest('[class*="menu-list"]') ||
                event.target.closest('[class*="-menu"]') ||
                event.target.closest('.select-filter-wrapper')
            ) {
                return false;
            }
        }
        return true;
    };

    return (
        <SelectWrapper
            ref={wrapperRef}
            width={props.width}
            {...rootProps}
            isDisable={props.isDisabled}
            className="select-filter-wrapper"
            onMouseEnter={updatePlacement}
            onMouseDown={updatePlacement}
            onTouchStart={updatePlacement}
            onFocus={updatePlacement}
        >
            <SelectComponent
                {...props}
                className="select-filter-component"
                menuPlacement={effectivePlacement}
                onMenuOpen={handleMenuOpen}
                menuPosition={props.menuPosition || (portalTarget ? "fixed" : "absolute")}
                menuPortalTarget={portalTarget}
                closeMenuOnScroll={props.closeMenuOnScroll !== undefined ? props.closeMenuOnScroll : (portalTarget ? defaultCloseOnScroll : false)}
                styles={{
                    control: base => ({
                        ...base,
                        width: "100%",
                        minHeight: height || "42px",
                        ...customStyles,
                    }),
                    container: base => ({
                        ...base,
                        width: "100%",
                        ...containerStyles,
                    }),
                    dropdownIndicator: base => ({
                        ...base,
                        color: dropdownColor || "#000000",
                    }),
                    menu: (provided) => ({
                        ...provided,
                        zIndex: 99999,
                        ...menuStyles,
                    }),
                    menuList: (provided) => ({
                        ...provided,
                        maxHeight: "200px",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#cbd5e1",
                            borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "#94a3b8",
                        },
                    }),
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 99999,
                    }),
                    ...styleProps,
                }}
                noOptionsMessage={() => noOptionMessage || 'No Data'}
                height={height}
                capitalize={capitalizeFirstChar}
                hasError={(warnMessage || {}).text}
                theme={theme => ({
                    ...theme,
                    borderRadius: borderRadius,
                    colors: {
                        ...theme.colors,
                        neutral50: props.placeholderColor || black400  // Placeholder color
                    },
                })}

            />
            {subText}
            {(warnMessage || {}).text &&
                <ErrorMessageStyle textAlignMent={warnMessage.align} >
                    {warnMessage.text}
                </ErrorMessageStyle>
            }
        </SelectWrapper>
    )
}